import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, PointerEvent as ReactPointerEvent, ReactNode } from "react";
import {
  closestCenter,
  DndContext,
  pointerWithin,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type { CollisionDetection } from "@dnd-kit/core";
import { SortableContext, useSortable, horizontalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { players as legacyBasePlayersArr, rankingIds as legacyRankingIds } from "../shared/data/rankings";
import { playerGameLogsById } from "../shared/data/playerGameLogs";
import { playerProfilesById } from "../shared/data/playerProfiles";
import type { Player, Position } from "../shared/models/Player";
import { useSleeperDirectory } from "../shared/hooks/useSleeperDirectory";
import { usePlayers } from "../shared/hooks/usePlayers";
import TeamLogo from "../shared/components/TeamLogo";
import TierChartPlayerProfileView from "./TierChartPlayerProfileModal";
import { posColor } from "../shared/utils/posColor";
import { formatTeamAbbreviation } from "../shared/utils/teamAbbreviation";
import { getTierLabel, getTierPalette } from "../shared/tierChart/tierPalette";

type TierChartPositionFilter = "Overall" | "QB" | "RB" | "WR" | "TE";
type PpgRankMode = "overall" | "position";

type TierChartTier = {
  id: string;
  label: string;
  color: string;
  shadowColor: string;
  playerIds: string[];
};

type PlayerSeasonSummary = {
  season: number | null;
  pprPpg: number | null;
  pprTotal: number | null;
  games: number;
};

type PlayerCardMetrics = PlayerSeasonSummary & {
  fppgRank: number | null;
};

type TierChartProfileFrame = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type ResizeDirection = "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";

type TierChartProfileInteraction =
  | {
      type: "drag";
      pointerId: number;
      startX: number;
      startY: number;
      originX: number;
      originY: number;
    }
  | {
      type: "resize";
      pointerId: number;
      startX: number;
      startY: number;
      originX: number;
      originY: number;
      originWidth: number;
      originHeight: number;
      direction: ResizeDirection;
    };

type DragOverlayState =
  | {
      playerId: string;
      chartRank: number | null;
    }
  | null;

const CHART_POSITIONS = new Set<Position>(["QB", "RB", "WR", "TE"]);
const PAGE_ZOOM_MIN = 0.72;
const PAGE_ZOOM_MAX = 1.38;
const PAGE_ZOOM_STEP = 0.08;
const PLAYER_CARD_WIDTH = 144;
const PLAYER_CARD_HEIGHT = 118;

function getDefaultProfileFrame(): TierChartProfileFrame {
  if (typeof window === "undefined") {
    return { x: 960, y: 90, width: 474, height: 588 };
  }

  const margin = 24;
  const top = Math.min(90, Math.max(margin, window.innerHeight * 0.08));
  const width = Math.min(474, Math.max(360, window.innerWidth - margin * 2));
  const height = Math.min(588, Math.max(360, window.innerHeight - top - margin));
  return {
    x: Math.max(margin, window.innerWidth - width - margin),
    y: top,
    width,
    height,
  };
}

function createTier(index: number): TierChartTier {
  const palette = getTierPalette(index);
  return {
    id: `tier-${index}-${Math.random().toString(36).slice(2, 8)}`,
    label: getTierLabel(index),
    color: palette.color,
    shadowColor: palette.shadowColor,
    playerIds: [],
  };
}

function relabelTiers(tiers: TierChartTier[]): TierChartTier[] {
  return tiers.map((tier, index) => {
    const palette = getTierPalette(index);
    return {
      ...tier,
      label: getTierLabel(index),
      color: palette.color,
      shadowColor: palette.shadowColor,
    };
  });
}

function getPprValue(row: { fantasyPointsPpr?: number; fantasyPoints?: number }) {
  return row.fantasyPointsPpr ?? row.fantasyPoints;
}

function summarizeLatestPprSeason(playerId: string): PlayerSeasonSummary {
  const seasons = [...(playerGameLogsById[playerId] ?? [])].sort((a, b) => b.season - a.season);

  for (const season of seasons) {
    const points = season.rows
      .filter((row) => row.week > 0)
      .map((row) => getPprValue(row))
      .filter((value): value is number => value != null && Number.isFinite(value) && value > 0);

    if (!points.length) continue;

    const pprTotal = points.reduce((sum, value) => sum + value, 0);
    return {
      season: season.season,
      pprPpg: pprTotal / points.length,
      pprTotal,
      games: points.length,
    };
  }

  return {
    season: null,
    pprPpg: null,
    pprTotal: null,
    games: 0,
  };
}

function formatDecimal(value: number | null | undefined) {
  if (value == null || !Number.isFinite(value)) return "-";
  return value.toFixed(1);
}

function getLastName(name: string) {
  const suffixes = new Set(["jr", "sr", "ii", "iii", "iv", "v"]);
  const tokens = name.replace(/\./g, "").split(/\s+/).filter(Boolean);
  while (tokens.length > 1 && suffixes.has(tokens[tokens.length - 1].toLowerCase())) {
    tokens.pop();
  }
  return (tokens[tokens.length - 1] ?? name).toUpperCase();
}

function getRankDisplay(player: Player, rank: number | null, mode: PpgRankMode) {
  if (!rank) return "-";
  return mode === "position" ? `${player.position}${rank}` : String(rank);
}

function hexToRgba(hex: string, alpha: number) {
  const normalized = hex.replace("#", "").trim();
  if (normalized.length !== 6) return `rgba(90,255,151,${alpha})`;
  const value = Number.parseInt(normalized, 16);
  if (!Number.isFinite(value)) return `rgba(90,255,151,${alpha})`;
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return `rgba(${r},${g},${b},${alpha})`;
}

function removePlayerFromTiers(tiers: TierChartTier[], playerId: string) {
  return tiers.map((tier) => ({
    ...tier,
    playerIds: tier.playerIds.filter((id) => id !== playerId),
  }));
}

function findPlayerLocation(tiers: TierChartTier[], playerId: string) {
  for (let tierIndex = 0; tierIndex < tiers.length; tierIndex += 1) {
    const playerIndex = tiers[tierIndex].playerIds.indexOf(playerId);
    if (playerIndex >= 0) return { tierIndex, playerIndex };
  }
  return null;
}

function insertPlayerIntoTier(tiers: TierChartTier[], tierId: string, playerId: string, insertIndex: number) {
  return tiers.map((tier) => {
    if (tier.id !== tierId) return tier;
    const nextPlayerIds = [...tier.playerIds];
    nextPlayerIds.splice(Math.max(0, Math.min(insertIndex, nextPlayerIds.length)), 0, playerId);
    return {
      ...tier,
      playerIds: nextPlayerIds,
    };
  });
}

function getDragEndPointer(event: DragEndEvent) {
  const activator = event.activatorEvent as Event & { clientX?: unknown; clientY?: unknown };
  if (typeof activator.clientX === "number" && typeof activator.clientY === "number") {
    return {
      x: activator.clientX + event.delta.x,
      y: activator.clientY + event.delta.y,
    };
  }
  return null;
}

function pointIsInsideElement(point: { x: number; y: number } | null, element: HTMLElement | null) {
  if (!point || !element) return false;
  const rect = element.getBoundingClientRect();
  return point.x >= rect.left && point.x <= rect.right && point.y >= rect.top && point.y <= rect.bottom;
}

function StatPill(props: { label: string; value: string; color: string }) {
  const { label, value, color } = props;
  return (
    <div
      style={{
        display: "grid",
        gap: 1,
        justifyItems: "end",
        lineHeight: 1,
        textShadow: "0 2px 0 #070707, 0 -1px 0 #070707, 1px 0 0 #070707, -1px 0 0 #070707",
      }}
    >
      <span style={{ fontSize: 8, fontWeight: 950, color: "rgba(255,255,255,0.74)" }}>{label}</span>
      <span style={{ fontSize: 15, fontWeight: 950, color }}>{value}</span>
    </div>
  );
}

function ZoomIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="10.5" cy="10.5" r="6.5" fill="none" stroke="currentColor" strokeWidth="2.6" />
      <path d="M15.5 15.5 21 21" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
    </svg>
  );
}

function ZoomButton(props: { label: string; onClick: () => void; disabled?: boolean; children: string }) {
  const { label, onClick, disabled = false, children } = props;
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      style={{
        appearance: "none",
        width: 24,
        height: 24,
        border: "none",
        borderRadius: 6,
        background: disabled ? "transparent" : "rgba(255,255,255,0.08)",
        color: disabled ? "rgba(255,255,255,0.35)" : "#fff",
        display: "inline-grid",
        placeItems: "center",
        fontSize: 18,
        fontWeight: 950,
        lineHeight: 1,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      {children}
    </button>
  );
}

function PlayerPortrait(props: { player: Player }) {
  const { player } = props;
  const [src, setSrc] = useState(player.imageUrl || "/headshot-placeholder.svg");
  const glowColor = hexToRgba(posColor(player.position), 0.72);

  return (
    <img
      src={src}
      alt={player.name}
      draggable={false}
      loading="lazy"
      decoding="async"
      onError={() => setSrc("/headshot-placeholder.svg")}
      style={{
        width: 138,
        height: 124,
        objectFit: "contain",
        objectPosition: "center bottom",
        filter: `drop-shadow(0 0 9px ${glowColor}) drop-shadow(0 10px 10px rgba(0,0,0,0.38))`,
        alignSelf: "end",
        justifySelf: "center",
        pointerEvents: "none",
      }}
    />
  );
}

function TierPlayerCard(props: {
  player: Player;
  metrics: PlayerCardMetrics;
  chartRank: number | null;
  ppgRankMode: PpgRankMode;
  zoom: number;
  dragging?: boolean;
  overlay?: boolean;
}) {
  const { player, metrics, chartRank, ppgRankMode, zoom, dragging = false, overlay = false } = props;
  const scaledWidth = PLAYER_CARD_WIDTH * zoom;
  const scaledHeight = PLAYER_CARD_HEIGHT * zoom;

  return (
    <div
      style={{
        width: scaledWidth,
        height: scaledHeight,
        position: "relative",
        flex: `0 0 ${scaledWidth}px`,
        opacity: dragging ? 0.42 : 1,
        transform: overlay ? "rotate(-1deg)" : undefined,
        cursor: overlay ? "grabbing" : "grab",
        userSelect: "none",
        touchAction: "none",
      }}
    >
      <div
        style={{
          width: PLAYER_CARD_WIDTH,
          height: PLAYER_CARD_HEIGHT,
          position: "relative",
          transform: `scale(${zoom})`,
          transformOrigin: "top left",
        }}
      >
        {chartRank != null ? (
        <div
          style={{
            position: "absolute",
            top: 20,
            left: 2,
            zIndex: 3,
            color: "#fff",
            fontSize: 19,
            fontWeight: 950,
            lineHeight: 1,
            textShadow: "0 3px 0 #070707, 0 -1px 0 #070707, 1px 0 0 #070707, -1px 0 0 #070707",
          }}
        >
          {chartRank}.
        </div>
        ) : null}

        <div
          style={{
            position: "absolute",
            left: 3,
            bottom: 8,
            zIndex: 5,
            width: 36,
            height: 36,
            display: "grid",
            placeItems: "center",
            filter: "drop-shadow(0 4px 5px rgba(0,0,0,0.62))",
          }}
        >
          <TeamLogo
            team={player.team}
            size={36}
            fallback={
              <span style={{ color: "#fff", fontWeight: 950 }}>
                {formatTeamAbbreviation(player.team, "FA")}
              </span>
            }
          />
        </div>

        <div
          style={{
            position: "absolute",
            top: 6,
            right: 2,
            zIndex: 3,
            display: "grid",
            gap: 0,
          }}
        >
          <StatPill label="AGE" value={player.age != null && player.age > 0 ? formatDecimal(player.age) : "-"} color="#61f369" />
          <StatPill label="PPR" value={formatDecimal(metrics.pprPpg)} color="#5ee9ff" />
          <StatPill label="PPG" value={getRankDisplay(player, metrics.fppgRank, ppgRankMode)} color="#fff05a" />
        </div>

        <div
          style={{
            position: "absolute",
            left: -6,
            right: -6,
            bottom: 1,
            zIndex: 2,
            display: "grid",
            justifyItems: "center",
          }}
        >
          <PlayerPortrait player={player} />
        </div>

        <div
          style={{
            position: "absolute",
            left: 20,
            right: 12,
            bottom: 4,
            zIndex: 4,
            minHeight: 20,
            borderRadius: 5,
            background: "rgba(7,8,12,0.88)",
            color: "#fff",
            display: "grid",
            placeItems: "center",
            padding: "2px 5px",
            fontSize: getLastName(player.name).length > 10 ? 13 : 16,
            fontWeight: 950,
            lineHeight: 1,
            textAlign: "center",
            textShadow: "0 2px 0 #000",
            boxShadow: "0 0 0 1px rgba(255,255,255,0.14)",
            overflow: "hidden",
          }}
        >
          {getLastName(player.name)}
        </div>
      </div>
    </div>
  );
}

function SortableTierPlayerCard(props: {
  player: Player;
  tierId: string;
  metrics: PlayerCardMetrics;
  chartRank: number;
  ppgRankMode: PpgRankMode;
  zoom: number;
  onOpenPlayer: (playerId: string) => void;
  shouldSuppressClick: () => boolean;
}) {
  const { player, tierId, metrics, chartRank, ppgRankMode, zoom, onOpenPlayer, shouldSuppressClick } = props;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `chart:${player.id}`,
    data: {
      type: "chart-player",
      playerId: player.id,
      tierId,
    },
  });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={(event) => {
        event.stopPropagation();
        if (isDragging || shouldSuppressClick()) return;
        onOpenPlayer(player.id);
      }}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 5 : 1,
      }}
    >
      <TierPlayerCard player={player} metrics={metrics} chartRank={chartRank} ppgRankMode={ppgRankMode} zoom={zoom} dragging={isDragging} />
    </div>
  );
}

function PoolPlayerCard(props: {
  player: Player;
  metrics: PlayerCardMetrics;
  ppgRankMode: PpgRankMode;
  zoom: number;
  onOpenPlayer: (playerId: string) => void;
  shouldSuppressClick: () => boolean;
}) {
  const { player, metrics, ppgRankMode, zoom, onOpenPlayer, shouldSuppressClick } = props;
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `pool:${player.id}`,
    data: {
      type: "pool-player",
      playerId: player.id,
    },
  });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={(event) => {
        event.stopPropagation();
        if (isDragging || shouldSuppressClick()) return;
        onOpenPlayer(player.id);
      }}
      style={{
        transform: CSS.Translate.toString(transform),
        zIndex: isDragging ? 20 : 1,
      }}
    >
      <TierPlayerCard player={player} metrics={metrics} chartRank={null} ppgRankMode={ppgRankMode} zoom={zoom} dragging={isDragging} />
    </div>
  );
}

function TierRow(props: {
  tier: TierChartTier;
  playersById: Record<string, Player>;
  metricsById: Record<string, PlayerCardMetrics>;
  chartRankById: Record<string, number>;
  ppgRankMode: PpgRankMode;
  zoom: number;
  onOpenPlayer: (playerId: string) => void;
  shouldSuppressClick: () => boolean;
}) {
  const { tier, playersById, metricsById, chartRankById, ppgRankMode, zoom, onOpenPlayer, shouldSuppressClick } = props;
  const rowMinHeight = 134 * zoom;
  const { setNodeRef, isOver } = useDroppable({
    id: `tier:${tier.id}`,
    data: {
      type: "tier",
      tierId: tier.id,
    },
  });

  return (
    <section
      style={{
        display: "grid",
        gridTemplateColumns: `${92 * zoom}px minmax(0, 1fr)`,
        gap: 12 * zoom,
        alignItems: "stretch",
      }}
    >
      <div
        style={{
          minHeight: rowMinHeight,
          borderRadius: 14,
          background: tier.color,
          boxShadow: `inset 0 -4px 0 ${tier.shadowColor}, 0 14px 26px ${tier.shadowColor}33`,
          display: "grid",
          placeItems: "center",
          color: "#fff",
          fontSize: 56 * zoom,
          fontWeight: 950,
          lineHeight: 1,
          textShadow: `0 4px 0 ${tier.shadowColor}, 0 0 2px rgba(0,0,0,0.48)`,
        }}
      >
        {tier.label}
      </div>

      <div
        ref={setNodeRef}
        style={{
          minHeight: rowMinHeight,
          borderRadius: 10,
          border: isOver ? "1px solid rgba(255,255,255,0.34)" : "1px solid rgba(255,255,255,0.08)",
          background: isOver ? "rgba(255,255,255,0.075)" : "rgba(255,255,255,0.032)",
          boxShadow: isOver ? "inset 0 0 0 1px rgba(255,255,255,0.10)" : undefined,
          padding: 8 * zoom,
          overflow: "hidden",
        }}
      >
        <SortableContext items={tier.playerIds.map((id) => `chart:${id}`)} strategy={horizontalListSortingStrategy}>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignContent: "flex-start",
              alignItems: "flex-start",
              gap: 8 * zoom,
              minHeight: 116 * zoom,
            }}
          >
            {tier.playerIds.map((playerId) => {
              const player = playersById[playerId];
              if (!player) return null;
              return (
                <SortableTierPlayerCard
                  key={playerId}
                  player={player}
                  tierId={tier.id}
                  metrics={metricsById[playerId]}
                  chartRank={chartRankById[playerId]}
                  ppgRankMode={ppgRankMode}
                  zoom={zoom}
                  onOpenPlayer={onOpenPlayer}
                  shouldSuppressClick={shouldSuppressClick}
                />
              );
            })}
          </div>
        </SortableContext>
      </div>
    </section>
  );
}

function SegmentButton(props: {
  active: boolean;
  children: ReactNode;
  onClick: () => void;
}) {
  const { active, children, onClick } = props;
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        appearance: "none",
        border: "1px solid rgba(255,255,255,0.12)",
        background: active ? "rgba(255,255,255,0.92)" : "rgba(255,255,255,0.055)",
        color: active ? "#111827" : "rgba(255,255,255,0.88)",
        borderRadius: 999,
        padding: "8px 13px",
        fontSize: 12,
        fontWeight: 950,
        lineHeight: 1,
        cursor: "pointer",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </button>
  );
}

function TierChartProfileOverlay(props: {
  player: Player | null;
  playersById: Record<string, Player>;
  rankingIds: string[];
  frame: TierChartProfileFrame;
  setFrame: React.Dispatch<React.SetStateAction<TierChartProfileFrame>>;
  onClose: () => void;
}) {
  const { player, playersById, rankingIds, frame, setFrame, onClose } = props;
  const interactionRef = useRef<TierChartProfileInteraction | null>(null);

  useEffect(() => {
    if (!player || typeof window === "undefined") return undefined;

    const clampFrame = (next: TierChartProfileFrame): TierChartProfileFrame => {
      const margin = 8;
      const minWidth = Math.min(420, Math.max(300, window.innerWidth - margin * 2));
      const minHeight = Math.min(420, Math.max(320, window.innerHeight - margin * 2));
      const maxWidth = Math.max(minWidth, window.innerWidth - margin * 2);
      const maxHeight = Math.max(minHeight, window.innerHeight - margin * 2);
      const width = Math.min(Math.max(next.width, minWidth), maxWidth);
      const height = Math.min(Math.max(next.height, minHeight), maxHeight);

      return {
        x: Math.min(Math.max(next.x, margin), Math.max(margin, window.innerWidth - width - margin)),
        y: Math.min(Math.max(next.y, margin), Math.max(margin, window.innerHeight - height - margin)),
        width,
        height,
      };
    };

    const onPointerMove = (event: PointerEvent) => {
      const interaction = interactionRef.current;
      if (!interaction || interaction.pointerId !== event.pointerId) return;

      const deltaX = event.clientX - interaction.startX;
      const deltaY = event.clientY - interaction.startY;

      setFrame((current) => {
        if (interaction.type === "drag") {
          return clampFrame({
            ...current,
            x: interaction.originX + deltaX,
            y: interaction.originY + deltaY,
          });
        }

        let next: TierChartProfileFrame = {
          x: interaction.originX,
          y: interaction.originY,
          width: interaction.originWidth,
          height: interaction.originHeight,
        };

        if (interaction.direction.includes("e")) next.width = interaction.originWidth + deltaX;
        if (interaction.direction.includes("s")) next.height = interaction.originHeight + deltaY;
        if (interaction.direction.includes("w")) {
          next.width = interaction.originWidth - deltaX;
          next.x = interaction.originX + deltaX;
        }
        if (interaction.direction.includes("n")) {
          next.height = interaction.originHeight - deltaY;
          next.y = interaction.originY + deltaY;
        }

        return clampFrame(next);
      });
    };

    const clearInteraction = () => {
      interactionRef.current = null;
    };

    const onResize = () => setFrame((current) => clampFrame(current));

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", clearInteraction);
    window.addEventListener("pointercancel", clearInteraction);
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", clearInteraction);
      window.removeEventListener("pointercancel", clearInteraction);
      window.removeEventListener("resize", onResize);
    };
  }, [player, setFrame]);

  const startDraggingProfile = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      interactionRef.current = {
        type: "drag",
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: frame.x,
        originY: frame.y,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [frame.x, frame.y]
  );

  const startResizingProfile = useCallback(
    (direction: ResizeDirection) => (event: ReactPointerEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      interactionRef.current = {
        type: "resize",
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: frame.x,
        originY: frame.y,
        originWidth: frame.width,
        originHeight: frame.height,
        direction,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [frame.height, frame.width, frame.x, frame.y]
  );

  if (!player) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 5000,
        pointerEvents: "none",
      }}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        style={{
          position: "absolute",
          left: frame.x,
          top: frame.y,
          width: frame.width,
          height: frame.height,
          borderRadius: 20,
          overflow: "hidden",
          border: "1px solid rgba(255,255,255,0.14)",
          background: "rgba(6,10,20,0.94)",
          boxShadow: "0 26px 70px rgba(0,0,0,0.46)",
          pointerEvents: "auto",
        }}
      >
        <TierChartPlayerProfileView
          presentation="embedded"
          player={player}
          links={playerProfilesById[player.id] ?? []}
          gameLogs={playerGameLogsById[player.id] ?? []}
          rankingIds={rankingIds}
          rankingsRankingIds={rankingIds}
          playersById={playersById}
          rankLabel="Tier Chart Rank"
          videoViewStamp="none"
          onToggleVideoViewScam={() => undefined}
          onToggleVideoViewCoin={() => undefined}
          onToggleVideoViewDiamond={() => undefined}
          hideVideoViewStampControls
          onStartVideoViewDrag={startDraggingProfile}
          onClose={onClose}
        />
        {[
          ["n", "ns-resize", { top: -6, left: 18, right: 18, height: 12 }],
          ["s", "ns-resize", { bottom: -6, left: 18, right: 18, height: 12 }],
          ["w", "ew-resize", { left: -6, top: 18, bottom: 18, width: 12 }],
          ["e", "ew-resize", { right: -6, top: 18, bottom: 18, width: 12 }],
          ["nw", "nwse-resize", { top: -8, left: -8, width: 18, height: 18 }],
          ["ne", "nesw-resize", { top: -8, right: -8, width: 18, height: 18 }],
          ["sw", "nesw-resize", { bottom: -8, left: -8, width: 18, height: 18 }],
          ["se", "nwse-resize", { bottom: -8, right: -8, width: 18, height: 18 }],
        ].map(([direction, cursor, position]) => (
          <button
            key={direction as string}
            type="button"
            aria-label={`Resize player profile ${direction}`}
            onPointerDown={startResizingProfile(direction as ResizeDirection)}
            style={{
              position: "absolute",
              appearance: "none",
              border: "none",
              background: "transparent",
              cursor: cursor as CSSProperties["cursor"],
              zIndex: 8,
              touchAction: "none",
              ...(position as CSSProperties),
            }}
          />
        ))}
      </div>
    </div>
  );
}

export default function TierChartPage() {
  const poolDropElementRef = useRef<HTMLElement | null>(null);
  const poolRowElementRef = useRef<HTMLDivElement | null>(null);
  const lastDragCompletedAtRef = useRef(0);
  const [tiers, setTiers] = useState<TierChartTier[]>(() => [createTier(0)]);
  const [activeTab, setActiveTab] = useState<TierChartPositionFilter>("Overall");
  const [search, setSearch] = useState("");
  const [ppgRankMode, setPpgRankMode] = useState<PpgRankMode>("overall");
  const [pageZoom, setPageZoom] = useState(1);
  const [dragOverlay, setDragOverlay] = useState<DragOverlayState>(null);
  const [profilePlayerId, setProfilePlayerId] = useState<string | null>(null);
  const [profileFrame, setProfileFrame] = useState<TierChartProfileFrame>(() => getDefaultProfileFrame());

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6,
      },
    })
  );

  const { sleeperPlayers, canonicalizePlayers, canonicalizeRankingIds } = useSleeperDirectory({
    legacyPlayers: legacyBasePlayersArr,
  });

  const basePlayers = useMemo(() => {
    const mergedById = new Map<string, Player>();
    for (const source of [canonicalizePlayers(legacyBasePlayersArr), sleeperPlayers]) {
      for (const player of source) {
        mergedById.set(player.id, { ...mergedById.get(player.id), ...player });
      }
    }
    return Array.from(mergedById.values());
  }, [canonicalizePlayers, sleeperPlayers]);

  const { playersById } = usePlayers({ basePlayers });

  const playerPoolIds = useMemo(() => {
    const rankedIds = canonicalizeRankingIds(legacyRankingIds, legacyBasePlayersArr);
    const seen = new Set<string>();
    const next: string[] = [];

    for (const id of rankedIds) {
      const player = playersById[id];
      if (!player || !CHART_POSITIONS.has(player.position) || seen.has(id)) continue;
      seen.add(id);
      next.push(id);
    }

    return next;
  }, [canonicalizeRankingIds, playersById]);

  const metricsBaseById = useMemo(() => {
    return Object.fromEntries(playerPoolIds.map((playerId) => [playerId, summarizeLatestPprSeason(playerId)]));
  }, [playerPoolIds]);

  const rankByIdOverall = useMemo(() => {
    const rows = playerPoolIds
      .map((playerId) => ({ playerId, ppg: metricsBaseById[playerId]?.pprPpg ?? null }))
      .filter((row): row is { playerId: string; ppg: number } => row.ppg != null && Number.isFinite(row.ppg))
      .sort((a, b) => b.ppg - a.ppg || a.playerId.localeCompare(b.playerId));

    return Object.fromEntries(rows.map((row, index) => [row.playerId, index + 1]));
  }, [metricsBaseById, playerPoolIds]);

  const rankByIdPosition = useMemo(() => {
    const rowsByPosition = new Map<Position, Array<{ playerId: string; ppg: number }>>();

    for (const playerId of playerPoolIds) {
      const player = playersById[playerId];
      const ppg = metricsBaseById[playerId]?.pprPpg ?? null;
      if (!player || ppg == null || !Number.isFinite(ppg)) continue;
      const rows = rowsByPosition.get(player.position) ?? [];
      rows.push({ playerId, ppg });
      rowsByPosition.set(player.position, rows);
    }

    const next: Record<string, number> = {};
    rowsByPosition.forEach((rows) => {
      rows
        .sort((a, b) => b.ppg - a.ppg || a.playerId.localeCompare(b.playerId))
        .forEach((row, index) => {
          next[row.playerId] = index + 1;
        });
    });

    return next;
  }, [metricsBaseById, playerPoolIds, playersById]);

  const metricsById = useMemo(() => {
    return Object.fromEntries(
      playerPoolIds.map((playerId) => [
        playerId,
        {
          ...metricsBaseById[playerId],
          fppgRank: ppgRankMode === "position" ? rankByIdPosition[playerId] ?? null : rankByIdOverall[playerId] ?? null,
        },
      ])
    );
  }, [metricsBaseById, playerPoolIds, ppgRankMode, rankByIdOverall, rankByIdPosition]);

  const placedPlayerIds = useMemo(() => new Set(tiers.flatMap((tier) => tier.playerIds)), [tiers]);

  const chartRankById = useMemo(() => {
    const next: Record<string, number> = {};
    let rank = 1;
    tiers.forEach((tier) => {
      tier.playerIds.forEach((playerId) => {
        next[playerId] = rank;
        rank += 1;
      });
    });
    return next;
  }, [tiers]);

  const profileRankingIds = useMemo(() => {
    const seen = new Set<string>();
    const next: string[] = [];

    for (const playerId of tiers.flatMap((tier) => tier.playerIds)) {
      if (seen.has(playerId)) continue;
      seen.add(playerId);
      next.push(playerId);
    }

    for (const playerId of playerPoolIds) {
      if (seen.has(playerId)) continue;
      seen.add(playerId);
      next.push(playerId);
    }

    return next;
  }, [playerPoolIds, tiers]);

  const visiblePoolIds = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return playerPoolIds.filter((playerId) => {
      if (placedPlayerIds.has(playerId)) return false;
      const player = playersById[playerId];
      if (!player) return false;
      if (activeTab !== "Overall" && player.position !== activeTab) return false;
      if (!normalizedSearch) return true;
      return `${player.name} ${player.team ?? ""} ${player.position}`.toLowerCase().includes(normalizedSearch);
    });
  }, [activeTab, placedPlayerIds, playerPoolIds, playersById, search]);

  const { setNodeRef: setPoolDropRef, isOver: isPoolOver } = useDroppable({
    id: "pool-tray",
    data: {
      type: "pool",
    },
  });
  const { setNodeRef: setPoolRowDropRef, isOver: isPoolRowOver } = useDroppable({
    id: "pool-row",
    data: {
      type: "pool",
    },
  });
  const isAnyPoolDropOver = isPoolOver || isPoolRowOver;

  const setPoolDropNode = useCallback(
    (node: HTMLElement | null) => {
      poolDropElementRef.current = node;
      setPoolDropRef(node);
    },
    [setPoolDropRef]
  );

  const setPoolRowDropNode = useCallback(
    (node: HTMLDivElement | null) => {
      poolRowElementRef.current = node;
      setPoolRowDropRef(node);
    },
    [setPoolRowDropRef]
  );

  const collisionDetection = useCallback<CollisionDetection>((args) => {
    const pointerCollisions = pointerWithin(args);
    return pointerCollisions.length > 0 ? pointerCollisions : closestCenter(args);
  }, []);

  const addTier = useCallback(() => {
    setTiers((current) => relabelTiers([...current, createTier(current.length)]));
  }, []);

  const zoomOut = useCallback(() => {
    setPageZoom((current) => Math.max(PAGE_ZOOM_MIN, Number((current - PAGE_ZOOM_STEP).toFixed(2))));
  }, []);

  const zoomIn = useCallback(() => {
    setPageZoom((current) => Math.min(PAGE_ZOOM_MAX, Number((current + PAGE_ZOOM_STEP).toFixed(2))));
  }, []);

  const openPlayerProfile = useCallback((playerId: string) => {
    setProfilePlayerId(playerId);
  }, []);

  const closePlayerProfile = useCallback(() => {
    setProfilePlayerId(null);
  }, []);

  const shouldSuppressCardClick = useCallback(() => {
    return Date.now() - lastDragCompletedAtRef.current < 280;
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const activeData = event.active.data.current as { type?: string; playerId?: string; tierId?: string } | undefined;
    const overData = event.over?.data.current as { type?: string; playerId?: string; tierId?: string } | undefined;
    const playerId = activeData?.playerId;

    setDragOverlay(null);
    lastDragCompletedAtRef.current = Date.now();

    if (!playerId) return;

    if (activeData?.type === "chart-player") {
      const pointer = getDragEndPointer(event);
      const droppedOnPool =
        pointIsInsideElement(pointer, poolRowElementRef.current) ||
        pointIsInsideElement(pointer, poolDropElementRef.current) ||
        overData?.type === "pool";

      if (droppedOnPool) {
        setTiers((current) => removePlayerFromTiers(current, playerId));
        return;
      }
    }

    if (!overData) return;

    if (overData.type === "pool") {
      if (activeData?.type !== "chart-player") return;
      setTiers((current) => removePlayerFromTiers(current, playerId));
      return;
    }

    const targetTierId = overData.type === "chart-player" ? overData.tierId : overData.type === "tier" ? overData.tierId : null;
    if (!targetTierId) return;

    setTiers((current) => {
      const targetTierIndex = current.findIndex((tier) => tier.id === targetTierId);
      if (targetTierIndex < 0) return current;

      const withoutPlayer = removePlayerFromTiers(current, playerId);
      const targetTier = withoutPlayer[targetTierIndex];
      let insertIndex = targetTier.playerIds.length;

      if (overData.type === "chart-player" && overData.playerId) {
        const overLocationBeforeRemoval = findPlayerLocation(current, overData.playerId);
        const overLocationAfterRemoval = findPlayerLocation(withoutPlayer, overData.playerId);
        if (overLocationAfterRemoval && overLocationAfterRemoval.tierIndex === targetTierIndex) {
          insertIndex = overLocationAfterRemoval.playerIndex;
          const activeLocation = findPlayerLocation(current, playerId);
          if (
            activeLocation &&
            overLocationBeforeRemoval &&
            activeLocation.tierIndex === overLocationBeforeRemoval.tierIndex &&
            activeLocation.playerIndex < overLocationBeforeRemoval.playerIndex
          ) {
            insertIndex += 1;
          }
        }
      }

      return insertPlayerIntoTier(withoutPlayer, targetTierId, playerId, insertIndex);
    });
  }, []);

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const activeData = event.active.data.current as { playerId?: string } | undefined;
      const playerId = activeData?.playerId;
      if (!playerId) return;
      setDragOverlay({
        playerId,
        chartRank: chartRankById[playerId] ?? null,
      });
    },
    [chartRankById]
  );

  const handleDragCancel = useCallback(() => {
    setDragOverlay(null);
    lastDragCompletedAtRef.current = Date.now();
  }, []);

  const overlayPlayer = dragOverlay?.playerId ? playersById[dragOverlay.playerId] : null;
  const profilePlayer = profilePlayerId ? playersById[profilePlayerId] ?? null : null;
  const pageBackground: CSSProperties = {
    background:
      "linear-gradient(180deg, rgba(7,9,14,0.88) 0%, rgba(7,9,14,0.58) 58%, rgba(66,36,14,0.72) 100%), radial-gradient(900px 420px at 72% 18%, rgba(255,255,255,0.06), transparent 62%), #111318",
  };

  return (
    <div
      className="appViewport"
      style={{
        ...pageBackground,
        display: "grid",
        gridTemplateRows: "auto minmax(0, 1fr) auto",
        gap: 12,
        paddingTop: 12,
        overflow: "hidden",
      }}
    >
      <DndContext sensors={sensors} collisionDetection={collisionDetection} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>
        <header
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              onClick={addTier}
              style={{
                appearance: "none",
                border: "1px solid rgba(255,255,255,0.18)",
                borderRadius: 8,
                background: "rgba(255,255,255,0.92)",
                color: "#111827",
                padding: "10px 13px",
                fontSize: 13,
                fontWeight: 950,
                lineHeight: 1,
                cursor: "pointer",
                boxShadow: "0 10px 22px rgba(0,0,0,0.22)",
              }}
            >
              Add Tier
            </button>

            <label
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                minHeight: 32,
                padding: "3px 4px 3px 10px",
                borderRadius: 8,
                border: "1px solid rgba(255,255,255,0.14)",
                background: "rgba(10,14,24,0.78)",
                color: "rgba(255,255,255,0.78)",
                fontSize: 12,
                fontWeight: 900,
                boxShadow: "0 10px 22px rgba(0,0,0,0.18)",
              }}
            >
              PPG Rank
              <select
                value={ppgRankMode}
                onChange={(event) => setPpgRankMode(event.target.value as PpgRankMode)}
                style={{
                  appearance: "none",
                  border: "1px solid rgba(255,255,255,0.24)",
                  borderRadius: 7,
                  background: "rgba(255,255,255,0.12)",
                  color: "#fff",
                  padding: "7px 30px 7px 11px",
                  fontSize: 12,
                  fontWeight: 900,
                  lineHeight: 1,
                  cursor: "pointer",
                  boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.18)",
                  backgroundImage:
                    "linear-gradient(45deg, transparent 50%, rgba(255,255,255,0.86) 50%), linear-gradient(135deg, rgba(255,255,255,0.86) 50%, transparent 50%)",
                  backgroundPosition: "calc(100% - 14px) 50%, calc(100% - 9px) 50%",
                  backgroundSize: "5px 5px, 5px 5px",
                  backgroundRepeat: "no-repeat",
                }}
              >
                <option value="overall">Overall</option>
                <option value="position">Position</option>
              </select>
            </label>

            <div
              aria-label="Page zoom controls"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 3,
                height: 32,
                padding: 2,
                borderRadius: 8,
                border: "1px solid rgba(255,255,255,0.14)",
                background: "rgba(10,14,24,0.78)",
                color: "rgba(255,255,255,0.78)",
                boxShadow: "0 10px 22px rgba(0,0,0,0.18)",
              }}
            >
              <ZoomButton label="Zoom in" onClick={zoomIn} disabled={pageZoom >= PAGE_ZOOM_MAX}>
                +
              </ZoomButton>
              <span
                title="Page zoom"
                style={{
                  width: 24,
                  height: 24,
                  display: "inline-grid",
                  placeItems: "center",
                  color: "rgba(255,255,255,0.88)",
                }}
              >
                <ZoomIcon />
              </span>
              <ZoomButton label="Zoom out" onClick={zoomOut} disabled={pageZoom <= PAGE_ZOOM_MIN}>
                -
              </ZoomButton>
            </div>
          </div>
        </header>

        <main
          className="playerProfileScroll"
          style={{
            minHeight: 0,
            overflow: "auto",
            display: "grid",
            alignContent: "start",
            gap: 10,
            paddingRight: 2,
          }}
        >
          {tiers.map((tier) => (
            <TierRow
              key={tier.id}
              tier={tier}
              playersById={playersById}
              metricsById={metricsById}
              chartRankById={chartRankById}
              ppgRankMode={ppgRankMode}
              zoom={pageZoom}
              onOpenPlayer={openPlayerProfile}
              shouldSuppressClick={shouldSuppressCardClick}
            />
          ))}
        </main>

        <footer
          ref={setPoolDropNode}
          style={{
            borderRadius: 10,
            border: isAnyPoolDropOver ? "1px solid rgba(255,255,255,0.32)" : "1px solid rgba(255,255,255,0.10)",
            background: isAnyPoolDropOver ? "rgba(255,255,255,0.08)" : "rgba(7,10,18,0.82)",
            boxShadow: "0 -14px 32px rgba(0,0,0,0.26)",
            padding: 10,
            display: "grid",
            gap: 10,
            minHeight: 44 + 118 * pageZoom,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 7, overflowX: "auto", scrollbarWidth: "none" }}>
              {(["Overall", "QB", "RB", "WR", "TE"] as TierChartPositionFilter[]).map((tab) => (
                <SegmentButton key={tab} active={activeTab === tab} onClick={() => setActiveTab(tab)}>
                  {tab}
                </SegmentButton>
              ))}
            </div>

            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search"
              style={{
                width: "min(280px, 100%)",
                minWidth: 180,
                border: "1px solid rgba(255,255,255,0.14)",
                borderRadius: 8,
                background: "rgba(255,255,255,0.08)",
                color: "#fff",
                padding: "9px 12px",
                fontSize: 13,
                fontWeight: 750,
                outline: "none",
              }}
            />
          </div>

          <div
            ref={setPoolRowDropNode}
            className="playerProfileScroll"
            style={{
              display: "flex",
              gap: 8 * pageZoom,
              overflowX: "auto",
              overflowY: "hidden",
              minHeight: 122 * pageZoom,
              paddingBottom: 2,
              scrollbarWidth: "none",
            }}
          >
            {visiblePoolIds.map((playerId) => {
              const player = playersById[playerId];
              if (!player) return null;
              return (
                <PoolPlayerCard
                  key={playerId}
                  player={player}
                  metrics={metricsById[playerId]}
                  ppgRankMode={ppgRankMode}
                  zoom={pageZoom}
                  onOpenPlayer={openPlayerProfile}
                  shouldSuppressClick={shouldSuppressCardClick}
                />
              );
            })}
          </div>
        </footer>

        <DragOverlay dropAnimation={null}>
          {overlayPlayer && dragOverlay ? (
            <TierPlayerCard
              player={overlayPlayer}
              metrics={metricsById[overlayPlayer.id]}
              chartRank={dragOverlay.chartRank}
              ppgRankMode={ppgRankMode}
              zoom={pageZoom}
              overlay
            />
          ) : null}
        </DragOverlay>
      </DndContext>
      <TierChartProfileOverlay
        player={profilePlayer}
        playersById={playersById}
        rankingIds={profileRankingIds}
        frame={profileFrame}
        setFrame={setProfileFrame}
        onClose={closePlayerProfile}
      />
    </div>
  );
}
